
import { ellipsisString, formatDate } from "@/app/infrastructures/misc/Utils";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { UrgentDeliveryManagementController } from "@/app/ui/controllers/UrgentDeliveryManagementController";
import { ApiStateData } from "@/domain/entities/Api";
import { ErrorMessageEntities, OptionsClass } from "@/domain/entities/MainApp";
import {
  LastLocationOption,
  UrgentDeliveryManagementDetailData,
  UrgentDeliveryManagementHALData,
  UrgentDeliveryManagementHALListRequest,
  UrgentDeliveryManagementHALListRequestDownload
} from "@/domain/entities/UrgentDeliveryManagement";
import { Options, Vue } from "vue-class-component";
import Detail from "../detail.vue";
import FilterHal from "./filter.vue";

@Options({
  components: {
    Detail,
    FilterHal
  },
})
class UrgentHAL extends Vue {
  controller = UrgentDeliveryManagementController;
  parameters = {
    search: "",
    onSort: false,
    cityCode: new OptionsClass(),
    hubId: new OptionsClass(),
  };
  modalDetail = false;
  apiDetailData = new ApiStateData();
  urgentDeliveryManagementDetailData = new UrgentDeliveryManagementDetailData();

  get columns() {
    return [
      {
        name: this.$t("urgentDeliveryManagement.hal.columnNo"),
        styleHead: "text-left head-no",
        render: (item: UrgentDeliveryManagementHALData, i: number) =>
          `<span class="${
            this.getStyleByFlag(item.pdFlag).class
          } font-normal text-black-lp-300 whitespace-nowrap">${i +
            1 +
            this.pagination.limit * (this.pagination.page - 1)}</span>`
      },
      {
        name: this.$t("urgentDeliveryManagement.hal.columnNoStt"),
        styleHead: "text-left head-no-stt",
        render: (item: UrgentDeliveryManagementHALData) =>
          `<div class="flex flex-col gap-1 text-sm text-black-lp-100">
          <div>
            ${item.pdSTTNo}
          </div>
        </div>`
      },
      {
        name: this.$t("urgentDeliveryManagement.hal.columnReason"),
        styleHead: "text-left head-reason",
        render: (item: UrgentDeliveryManagementHALData) =>
          `<div class="font-normal text-sm text-black-lp-100">${item.pdReason}</div>`
      },
      {
        name: this.$t("urgentDeliveryManagement.hal.columnOriginCity"),
        styleHead: "text-left head-origin",
        render: (item: UrgentDeliveryManagementHALData) =>
          `<div class="font-normal text-sm text-black-lp-100">${item.sttOriginCityId} - ${item.sttOriginCityName}</div>`
      },
      {
        name: this.$t("urgentDeliveryManagement.hal.columnLastStatus"),
        styleHead: "text-left head-last-status",
        render: (item: UrgentDeliveryManagementHALData) =>
          `<div class="font-normal text-sm text-black-lp-100">${item.sttLastStatusId}</div>`
      },
      {
        name: this.$t("urgentDeliveryManagement.hal.columnLastLocation"),
        styleHead: "text-left w-60",
        render: (item: UrgentDeliveryManagementHALData) =>
          `<div class="overflow-ellipsis text-left font-normal text-sm">
            ${
              item.hubName
                ? `${item.cityCode} - ${item.hubName}`
                : item.cityCode || "-"
            }
          </div>
          <span class="text-xs text-gray-lp-300">
            Station: ${item.stationBucket || "-"}
          </span>`
      },
      {
        name: this.$t("urgentDeliveryManagement.hal.columnRequestTime"),
        styleHead: "text-left head-request-time",
        render: (item: UrgentDeliveryManagementHALData) =>
          `<div class="font-normal text-sm text-black-lp-100">${this.getCreatedAtFormatDate(
            item.pdCreatedAt
          )}</div>
        <div class="capitalize text-gray-lp-500 break-all">${ellipsisString(
          item.createdBy,
          20
        )}</div>`
      }
    ];
  }

  mounted(): void {
    this.fetchList(true);
    this.controller.resetLastLocationOptions();
    this.controller.getLastLocationOptions();
  }

  fetchList(reset = false): void {
    if (reset) {
      this.controller.paginationData.page = 1;
    }

    this.controller.getHALList(
      new UrgentDeliveryManagementHALListRequest({
        sortBy: this.parameters.onSort ? "asc" : "desc",
        sttNo: this.parameters.search,
        page: this.controller.paginationData.page,
        limit: this.controller.paginationData.limit,
        cityCode: this.parameters.cityCode.value,
        hubId: this.parameters.hubId.value,
      })
    );
  }

  onSearch(value: string) {
    if (!value.length || value.length >= 3) {
      this.parameters.search = value;
      this.fetchList(true);
    }
  }

  clearSearch() {
    this.parameters.search = "";
    this.fetchList(true);
  }

  getStyleByFlag(
    flag: string
  ): {
    class: string;
    icon: string;
    textClass: string;
  } {
    if (
      flag
        .toLowerCase()
        .includes(this.$t("urgentDeliveryManagement.hal.flagToday"))
    ) {
      return {
        class: "today",
        icon: "clock-normal-red.svg",
        textClass: "text-red-lp-100"
      };
    }
    if (
      flag
        .toLowerCase()
        .includes(this.$t("urgentDeliveryManagement.hal.flagOverdue"))
    ) {
      return {
        class: "overdue",
        icon: "clock-exclamation.svg",
        textClass: "text-yellow-lp-100"
      };
    } else {
      return {
        class: "normal",
        icon: "clock-normal.svg",
        textClass: "text-gray-lp-500"
      };
    }
  }

  getCreatedAtFormatDate(date: string): string {
    return formatDate(date);
  }

  get sortButtonStyle() {
    return this.parameters.onSort
      ? {
          textColor: "red-lp-300",
          customClass: "w-full mr-4 bg-red-lp-1300 btn-active",
          color: "red-lp-300"
        }
      : {
          textColor: "gray-lp-500",
          customClass: "w-full mr-4",
          color: ""
        };
  }

  get emptyStateData(): {
    headerMessage: string;
    message: string;
    icon: string;
  } {
    if (this.parameters.search.length) {
      return {
        headerMessage: this.$t("urgentDeliveryManagement.empty.filterNotFound"),
        message: this.$t(
          "urgentDeliveryManagement.empty.filterNotFoundMessage"
        ),
        icon: "noData_filter"
      };
    } else {
      return {
        headerMessage: this.$t("urgentDeliveryManagement.empty.header"),
        message: this.$t("urgentDeliveryManagement.empty.message"),
        icon: "noData_empty"
      };
    }
  }

  get pagination() {
    return this.controller.paginationData;
  }

  async onDownload() {
    await this.controller.downloadUrgentHALList(
      new UrgentDeliveryManagementHALListRequestDownload({
        sortBy: this.parameters.onSort ? "asc" : "desc",
        sttNo: this.parameters.search,
        cityCode: this.parameters.cityCode.value,
        hubId: this.parameters.hubId.value
      })
    );
  }

  async goToDetail(item: UrgentDeliveryManagementHALData) {
    await this.getDetailData(item.pdId);
  }

  closeDetail() {
    this.modalDetail = false;
  }

  async getDetailData(id: number) {
    MainAppController.showLoading();
    try {
      this.urgentDeliveryManagementDetailData = await UrgentDeliveryManagementController.getPriorityDeliveryManagementDetail(
        id
      );
      this.modalDetail = true;
      this.apiDetailData.errorType = "";
    } catch (error) {
      MainAppController.closeLoading();
      MainAppController.showErrorMessage(
        new ErrorMessageEntities({
          type: "client-error",
          title: this.$t("urgentDeliveryManagement.error.getDetailFailed"),
          onClose: () => {
            MainAppController.closeErrorMessage();
          }
        })
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  get lastLocationOptions(): LastLocationOption[] {
    return this.controller.lastLocationOptions;
  }

  onApplyFilter(payload: {
    lastLocations?: { cityCodes: string[]; hubIds: number[] };
  }) {
    if (payload.lastLocations) {
      this.parameters.cityCode = new OptionsClass({
        value: payload.lastLocations.cityCodes.join(",")
      });
      this.parameters.hubId = new OptionsClass({
        value: payload.lastLocations.hubIds.map(String).join(",")
      });
    }
    this.fetchList(true);
  }

  onResetFilter() {
    this.parameters.cityCode = new OptionsClass();
    this.parameters.hubId = new OptionsClass();

    this.fetchList(true);
  }
}

export default UrgentHAL;
